import type { KeyboardEvent } from 'react';

/** What Tab can land on inside a dialog: links included, since a candidate's order number is one. */
const TABBABLE = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled):not([type="hidden"])',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Keeps Tab and Shift+Tab cycling inside a react-bootstrap modal. Its own focus enforcement only pulls
 * focus back once it lands on another element of the page, so Tab past the last control left for the
 * browser's toolbar, where Escape no longer reaches the page and the dialog could not be closed. Wrapping
 * at both ends keeps focus on something Escape can close from. Attach as the modal's `onKeyDown`.
 */
export const trapTabFocus = (event: KeyboardEvent<HTMLElement>) => {
  if (event.key !== 'Tab') {
    return;
  }
  const tabbable = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(TABBABLE));
  const first = tabbable[0];
  const last = tabbable[tabbable.length - 1];
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  // Focus on the dialog frame itself (where the modal opens) sits before the first control.
  const beforeFirst = active === first || !tabbable.includes(active as HTMLElement);
  if (event.shiftKey && beforeFirst) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};
